import PQueue from "p-queue"
import { store } from "@/store/store"
import {
	displayedPhotosAtom,
	getRandomPhotosForStylePreview,
	photosAtom,
} from "@/store/photo/photos-state"
import {
	derivePresetWithUserAdjustmentsForAI,
	loadPresetAdjustments,
	nullPresetID,
} from "@/store/store-presets"
import { getImageInfo } from "@/store/processing/image-info"
import { ProjectItem } from "@/store/project/projects-service"
import { StyleRecord, fetchKeyValue, storeStyleLut, getStyleLut } from "@/storage/indexeddb"
import { MainLUTResolver } from "@/io/assets/lut/lut-resolver"
import { projectAtom } from "@/store/project/projects-state"
import { applyStyleWithPreClustering } from "@/store/style/apply-style"
import { uuid } from "@/utils/uuid"
import {
	isLocalStyle,
	saveAsCurrentStyle,
	showStylePreviewAndNamingDialog,
	showStyleProcessingDialog,
	Style,
	StyleDraft,
} from "./style-state"
import {
	getIndividualAndMergedPresetAdjustments,
	getInputColorMatchFeatureWithFaceData,
	isParamsEmpty,
} from "../processing/processing-utils"
import { extractEmbeddedAdjustmentsFromXMP, getDefaultAdjustments } from "@/core/adjustments"
import { Adjustments, ColorProfileName, LookTable } from "@/core/types"
import {
	ImageInfoRecord,
	PhotoRecord,
	StyleInfoRecord,
	StyleReferenceRecord,
} from "@/lib/catalog/types"
import { Sentry } from "@/telemetry/sentry"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer i18n
import i18n from "@/i18n"
import { fetchCurrentAdjustmentsFromHistory } from "../store-history"
import { approveJpegReference } from "../processing/mark-reference"
import { PresetItem } from "../presets/presets-service"
import { queryClient } from "@/api/constants"
import { GetUserResponseBody } from "@/api/dto/users"
import { capitalized } from "@/utils/string"
import { PhotoId } from "../undoable-state"
import { parseCameraRawSettings, convertCameraRawSetting } from "@/core/utils/camera-raw-settings"
import { getFileId, isHEICFile } from "@/utils/file"
import { getBitmapSourceFromBlob } from "@/utils/bitmap-source"
import { PhotoResponseDTO } from "@/api/PSApiOperations"
import { formattedPSImagePathToUrl } from "../Pixieset/pixieset-image-urls"
import { logAiInfo } from "@/lib/ai/utils"
import { AI } from "@/lib/ai/ai"
import {
	getDistanceMap,
	getKNN,
	getUseLRParamsForJpegReference,
	getValidNearestRef,
	imageWbDistance,
} from "@/lib/ai/calibration/ai-calibration-feature"
import { webpDataToBitmap } from "@/core/utils/image-data"
import { estimateNeuralColorMatchParams } from "@/lib/ai/color-match/parameters"
import {
	getWeightedRefFeatures,
	InputColorMatchFeaturesWithFaceData,
	isNearlyBW,
	ReferenceColorMatchFeatures,
} from "@/lib/ai/color-match/utils"
import {
	fitToSize,
	resizeImageBitmapToBitmap,
	resizeImageBitmapToSize,
} from "@/core/utils/resize-image"
import { generateSampleImageInfos, loadSampleImageInfos } from "./sample-inputs"
import { pushDialog, popDialog, replaceDialog } from "../dialogs-state"
import { currentStyleCreationIdAtom } from "./style-state"
import { ConfirmDialog } from "@/components/Dialogs/ConfirmDialog"
import { DialogProps } from "@/components/ui/Dialog"
import { CatalogManager } from "@/lib/catalog"
import { closeSession } from "@/routing/project-session"
import { LoadingDialog } from "@/components/Dialogs/LoadingDialog"
import { MissingEditMetadataDialog } from "@/components/Dialogs/MissingEditMetadataDialog"
import { STYLE_COVER_LONGEST_SIDE, STYLE_COVER_OUTPUT_QUALITY } from "./style-cover-constants"
import { isProjectSetupIncomplete } from "../project/project"
import { fetchWithNetworkBehavior } from "@/api/fetch-with-network-behavior"
import { popActiveLoadingDialogs } from "@/store/loading-dialog-utils"
import { publishStyleDraft } from "./publish-style"
import { isNetworkConnectivityError, showActiveNetworkErrorToast } from "@/api/network-behavior"
import { getReferenceWebpData256x256 } from "@/lib/style/reference-webp-data"
import { AxiosError } from "axios"
import { isDuplicateStyleNameError } from "./style-service"

const STYLE_REFERENCE_MAX_LONGEST_SIDE = 1600

export function getDefaultStyleName() {
	const fallback = "Default AI Style"
	const user = queryClient.getQueryData<GetUserResponseBody>(["user"])

	if (!user) {
		return fallback
	}

	const { displayName, email } = user
	const username = displayName ? displayName.split(" ")[0] : email.split("@")[0]
	return username ? `${capitalized(username)}'s AI Style` : fallback
}

export async function createAIStyleFromEdits(styleName: string) {
	const currentProject = store.get(projectAtom)

	if (!currentProject) {
		return
	}

	const photos = store.get(photosAtom)

	const { stylePresets, referencesMap } = await getPresetsAndReferencesMapFromMarkedRefs({
		photos: Object.values(photos),
	})

	const styleDraft = {
		name: styleName,
		presets: stylePresets,
		referenceImages: referencesMap,
	}

	const dialogProps: DialogProps = {
		trackingViewName: "",
		canGoBack: false,
		disableClose: true,
	}
	const avoidingReferenceImages = Object.keys(referencesMap)
	const samplePhotos = getRandomPhotosForStylePreview(avoidingReferenceImages)

	const finalizeStyle = async (finalStyle: Style) => {
		try {
			let styleInfo: StyleInfoRecord
			let references: StyleReferenceRecord[]
			if (isLocalStyle(finalStyle)) {
				replaceDialog(LoadingDialog, {
					title: i18n.t("store:createStyle.generatingTitle"),
					message: i18n.t("store:createStyle.generatingMessage"),
					trackingViewName: "generate_style_loading_dialog",
				})
				styleInfo = finalStyle
				references = await CatalogManager.fetchStyleReferences(styleInfo.styleId)
			} else {
				const publishResult = await publishStyleDraft(finalStyle)
				styleInfo = publishResult.styleInfo
				references = publishResult.references
			}
			await saveAsCurrentStyle(styleInfo, references)

			console.log(`[Calibration] created style`, styleInfo.styleId)

			const displayedPhotos = store.get(displayedPhotosAtom)

			// Use pre-clustering wrapper with prioritized image IDs (reference images)
			// Pass showStyleProcessingDialog as callback to show after clustering completes/skips
			const prioritizeImageIds = Object.keys(referencesMap)
			applyStyleWithPreClustering(
				displayedPhotos,
				styleInfo,
				prioritizeImageIds,
				showStyleProcessingDialog,
				"finish_ai_style_setup",
			)
		} catch (error) {
			if (isDuplicateStyleNameError(error)) {
				throw error
			}

			console.error("Error creating style from edits:", error)
			popActiveLoadingDialogs()
			if (error instanceof AxiosError && error.response?.status === 401) {
				return
			}
			if (isNetworkConnectivityError(error)) {
				showActiveNetworkErrorToast()
				return
			}
			Sentry.captureException(error)

			// For other errors, show error dialog
			replaceDialog(ConfirmDialog, {
				title: i18n.t("store:createStyle.failedGenerateTitle"),
				message: i18n.t("store:createStyle.tryAgainLater"),
				primary: { title: i18n.t("common:action.ok") },
				trackingViewName: "generate_style_failure_dialog",
			})
		}
	}

	showStylePreviewAndNamingDialog(
		{ ...dialogProps, disableClose: true },
		styleDraft,
		"Editing",
		samplePhotos,
		finalizeStyle,
	)
}

// Note: this function only works for explicitly marked references in the project
// It doesn't work for implicit references, because it checks photo.kind and use getImageInfo
export async function getPresetsAndReferencesMapFromMarkedRefs(options: {
	photos: PhotoRecord[]
	existingPresetsMap?: Record<string, Partial<Adjustments>>
	existingReferencesMap?: Record<string, ImageInfoRecord>
}): Promise<{
	stylePresets: Record<string, Partial<Adjustments>>
	referencesMap: Record<string, ImageInfoRecord>
}> {
	const { photos, existingPresetsMap, existingReferencesMap } = options
	const referencesMap: Record<string, ImageInfoRecord> = existingReferencesMap ?? {}
	const stylePresets: Record<string, Partial<Adjustments>> = existingPresetsMap ?? {}

	for (const photo of photos) {
		const imageInfo = getImageInfo(photo.id)
		if (photo.kind !== "reference" || !imageInfo) {
			continue
		}
		referencesMap[photo.id] = imageInfo

		const presetId = imageInfo.presetId
		if (presetId && stylePresets[presetId] === undefined) {
			const presetAdjustments = await getOrDerivePresetAdjustments({
				presetId,
				imageInfo,
				photo,
				stylePresets,
			})

			// Store presetAdjustments with aggregated presetId-with-strength to style.presets
			stylePresets[presetId] = presetAdjustments

			// Also store strength=1 presetAdjmts for individual preset to style.presets - disabled to save space
			// stylePresets = { ...stylePresets, ...individualPresetMap }
		}
	}

	return { stylePresets, referencesMap }
}

// This function works for both explicit and implicit references
export async function getUpdatedStylePresetsMap(options: {
	references: StyleReferenceRecord[] // could be newly added or all references
	existingPresetsMap?: Record<string, Partial<Adjustments>>
}): Promise<Record<string, Partial<Adjustments>>> {
	const { references, existingPresetsMap } = options
	const stylePresets: Record<string, Partial<Adjustments>> = { ...(existingPresetsMap ?? {}) }

	const photos = store.get(photosAtom)

	for (const reference of references) {
		const imageInfo = reference.imageInfo
		const presetId = imageInfo.presetId
		if (presetId && stylePresets[presetId] === undefined) {
			const photo = photos[reference.imageInfo.imageId]
			stylePresets[presetId] = await getOrDerivePresetAdjustments({
				presetId,
				imageInfo,
				photo,
				stylePresets,
			})
		}
	}

	return stylePresets
}

async function getOrDerivePresetAdjustments(options: {
	presetId: string
	imageInfo: ImageInfoRecord
	photo: PhotoRecord | undefined
	stylePresets: Record<string, Partial<Adjustments>>
}): Promise<Partial<Adjustments>> {
	const { imageInfo, photo, presetId, stylePresets } = options

	let presetAdjustments =
		stylePresets[presetId] ??
		(imageInfo.presetAdjustmentsMap ? imageInfo.presetAdjustmentsMap[presetId] : undefined)
	if (!presetAdjustments && presetId === nullPresetID) {
		presetAdjustments = {}
	}
	if (presetAdjustments) return presetAdjustments

	if (!photo) {
		const message = `[getOrDerivePresetAdjustments] Could not find preset and photo for presetId: ${presetId} and imageId: ${imageInfo.imageId}`
		console.warn(message)
		Sentry.captureMessage(message, "warning")
		return {}
	}
	const defaultAdjustments = getDefaultAdjustments(photo)
	if (!presetAdjustments) {
		const presetMap = { ...imageInfo.presetAdjustmentsMap, ...stylePresets }
		const { mergedAdjustments } = await getIndividualAndMergedPresetAdjustments(
			presetMap,
			presetId,
			defaultAdjustments,
		)
		presetAdjustments = mergedAdjustments
	}
	if (!presetAdjustments) {
		const message = `[createAIStyle] Could not find preset adjustments for presetId: ${presetId}`
		console.warn(message)
		Sentry.captureMessage(message, "warning")

		// fallback to derive preset adjustments from history (user's global adjustments): less accurate
		const userAdjustments = await fetchCurrentAdjustmentsFromHistory(photo)
		const res = derivePresetWithUserAdjustmentsForAI({
			userAdjustments,
			presetLocalAdjustments: [],
			defaultAdjustments,
		})
		presetAdjustments = res.adjustments
	}

	return presetAdjustments
}

export function copyStyle(style: StyleRecord, project: ProjectItem): StyleRecord {
	const id = uuid()
	const date = new Date()
	return { ...style, id, projectId: project.id, createdAt: date, updatedAt: date }
}

export type RefImageItem = {
	bitmap: ImageBitmap
	photoId: string
	adjustments?: Partial<Adjustments>
	isPolarrAdjustments?: boolean
	coverSourceBlob?: Blob
	coverSourceIsHEIC?: boolean
}

type HslParameterStats = {
	mean: number
	median: number
	std: number
	q25: number
	q75: number
	iqr: number
	normalRange: [number, number]
	extremeThreshold: [number, number]
}

type HslStats = Record<string, HslParameterStats>

type PresetStatsData = {
	stats: HslStats
	sampleCount: number
	useGlobalFallback: boolean
}

// Create a new style from a list of jpeg references
// Todo: currently this function could take long if there are many bitmaps passed in, to optimize, maybe we should call `await approveJpegReference({ bitmap, photoId, preset: undefined })` in a queue outside of this function, and not pass in a list of bitmaps here (only pass in a list of photoIds).
async function createStyleDraftFromJpegReferences(options: {
	styleName: string
	refImages: RefImageItem[]
	basePreset?: PresetItem
	onProgress?: (progressDelta: number) => void
}) {
	const styleCreationIncludePreset = Boolean(options.basePreset)
	const basePreset = options.basePreset
	let refImages = options.refImages

	logAiInfo("[createAIStyleFromJpegReferences] basePreset", basePreset)
	if (basePreset && !basePreset.adjustments) {
		basePreset.adjustments = await loadPresetAdjustments(basePreset)
	}

	// Copy the base preset's LUT into the style LUT store so the publish flow
	// can find it (preset LUTs live under a different IndexedDB key).
	// Check preset IDB cache → MainLUTResolver (catalog/renderer) as fallback.
	if (basePreset?.adjustments?.userLutId) {
		const lutId = basePreset.adjustments.userLutId
		if (!(await getStyleLut(lutId))) {
			const presetLutEntry = await fetchKeyValue(`preset-adjustments-lut|${basePreset.id}`)
			const lut = presetLutEntry?.value ?? (await MainLUTResolver.get(lutId))
			if (lut) {
				await storeStyleLut(lut as { id: string })
			}
		}
	}

	// Remove ref images with embedded BW adjustments
	refImages = removeRefWithBWAdjustments(refImages)

	// If use embedded adjustments, normalize it first
	if (getUseLRParamsForJpegReference()) {
		normalizeAdjustments(refImages)
	} else {
		refImages.forEach((refImage) => {
			refImage.adjustments = undefined
		})
	}

	const referencesMap: Record<string, ImageInfoRecord> = {}
	const imagesData: Record<string, Blob> = {}
	const stylePresets: Record<string, Partial<Adjustments>> = {}
	const approvedPhotoIds: PhotoId[] = []
	for (const refImage of refImages) {
		const { bitmap, photoId } = refImage

		const imageInfo = await approveJpegReference({
			bitmap,
			photoId,
			adjustments: refImage.adjustments,
			isPolarrAdjustments: refImage.isPolarrAdjustments,
			approvedPhotoIds,
			preset: basePreset,
		})

		referencesMap[photoId] = imageInfo
		const presetId = imageInfo.presetId
		if (presetId) {
			stylePresets[presetId] = imageInfo.presetAdjustmentsMap?.[presetId] ?? {}
		}

		approvedPhotoIds.push(photoId)
		const resizedImage = await resizeImageBitmapToSize({
			imageBitMap: bitmap,
			longestSide: 512,
			result: ["blob"],
		})
		if (resizedImage.blob) {
			imagesData[photoId] = resizedImage.blob
		}

		if (refImage === refImages[refImages.length - 1]) {
			let coverBitmap: ImageBitmap | undefined
			if (refImage.coverSourceBlob) {
				const coverSource = await getBitmapSourceFromBlob(
					refImage.coverSourceBlob,
					Boolean(refImage.coverSourceIsHEIC),
				)
				coverBitmap = await createImageBitmap(coverSource)
			}

			const resizedCoverImage = await resizeImageBitmapToSize({
				imageBitMap: coverBitmap ?? bitmap,
				longestSide: STYLE_COVER_LONGEST_SIDE,
				result: ["blob"],
				outputQuality: STYLE_COVER_OUTPUT_QUALITY,
			})
			if (resizedCoverImage.blob) {
				imagesData["cover"] = resizedCoverImage.blob
			}
			coverBitmap?.close()
		}

		bitmap.close()
		options.onProgress?.(1)
	}

	// Remove low-saturation (BW or near-BW) ref images based on colorMatchReference.satLumStats
	removeNearlyBWRef(referencesMap)

	// find a base average HSL adjustments from the refImages if they don't have adjustments
	await setAverageAdjustments(referencesMap)

	const styleDraft: StyleDraft = {
		name: options.styleName,
		presets: stylePresets,
		referenceImages: referencesMap,
		imagesData: imagesData,
		styleCreationIncludePreset,
	}
	return styleDraft
}

export const extractAdjustmentsFromImage = async (
	fileOrBlob: File | Blob,
): Promise<
	| { adjustments: Partial<Adjustments>; isPolarrAdjustments: boolean; userLut?: LookTable }
	| undefined
> => {
	const arrayBuffer = await fileOrBlob.arrayBuffer()

	const buffer = new Uint8Array(arrayBuffer)
	const startMarker = new TextEncoder().encode("<x:xmpmeta")
	const endMarker = new TextEncoder().encode("</x:xmpmeta>")

	const findIndex = (buf: Uint8Array, sub: Uint8Array): number => {
		for (let i = 0; i <= buf.length - sub.length; i++) {
			let match = true
			for (let j = 0; j < sub.length; j++) {
				if (buf[i + j] !== sub[j]) {
					match = false
					break
				}
			}
			if (match) return i
		}
		return -1
	}

	const start = findIndex(buffer, startMarker)
	const end = findIndex(buffer, endMarker)

	if (start !== -1 && end !== -1) {
		const xmpEnd = end + endMarker.length
		const xmpSlice = buffer.slice(start, xmpEnd)
		const xmpString = new TextDecoder("utf-8").decode(xmpSlice)

		// Todo: after supporting storing LUT in the cloud, download the LUT data from
		// the Id/URL in the xmpString; cache it in the local catalog to avoid repetitive downloads
		let adjustments = extractEmbeddedAdjustmentsFromXMP(xmpString)
		let isPolarrAdjustments = true
		let userLut: LookTable | undefined
		if (!adjustments) {
			const settings = parseCameraRawSettings(xmpString)
			const result = convertCameraRawSetting(settings)
			adjustments = result.adjustments
			userLut = result.userLut
			isPolarrAdjustments = false
		}
		return { adjustments, isPolarrAdjustments, userLut }
	}

	return undefined
}

export const confirmCancelStyleCreation = () => {
	return new Promise<boolean>((resolve) => {
		pushDialog(ConfirmDialog, {
			trackingViewName: "cancel_create_style_confirmation",
			title: i18n.t("store:createStyle.cancelStyleCreationTitle"),
			message: i18n.t("store:createStyle.cancelStyleCreationMessage"),
			secondary: {
				title: i18n.t("store:createStyle.cancelStyleCreationTitle"),
				handler: () => {
					store.set(currentStyleCreationIdAtom, null)
					popDialog()
					const project = store.get(projectAtom)
					if (isProjectSetupIncomplete(project)) {
						void closeSession({ reason: "cancel_style_creation_in_project_setup" })
					}
					resolve(true)
				},
			},
			primary: { title: i18n.t("common:action.continue"), handler: () => resolve(false) },
		})
	})
}

const selectPresetForStyleCreation = () => {
	return new Promise<PresetItem | undefined>((resolve) => {
		pushDialog(MissingEditMetadataDialog, {
			trackingViewName: "select_optional_preset_for_style_dialog",
			onSelect: (preset) => {
				resolve(preset)
				popDialog()
			},
			canGoBack: false,
			backConfirm: confirmCancelStyleCreation,
			closeConfirm: confirmCancelStyleCreation,
			skip: () => {
				resolve(undefined)
				popDialog()
			},
			title: i18n.t("store:createStyle.missingEditMetadataTitle"),
			description: i18n.t("components:createStyle.weDidNotDetectEditing"),
		})
	})
}

export const createStyleDraftFromLegacyStyle = (styleRecord: StyleRecord) => {
	const styleDraft: StyleDraft = {
		name: styleRecord.name,
		presets: styleRecord.presets,
		referenceImages: styleRecord.referenceImages,
	}
	return styleDraft
}

export const createStyleDraftFromFiles = async (options: {
	styleCreationId: string
	styleName: string
	files: File[]
	onProgress?: (progressDelta: number) => void
	onThumbnail?: (index: number, blob: Blob) => void
	skipPreset?: boolean
}) => {
	const refImages = await convertFilesToRefImages(
		options.styleCreationId,
		options.files,
		options.onProgress,
		options.onThumbnail,
	)

	if (refImages.length === 0) {
		throw new Error("No valid reference images found.")
	}

	if (!checkIfStyleCreationIsCurrent(options.styleCreationId)) {
		// Style creation has been cancelled
		throw new Error("Style creation cancelled or restarted. Stopping current process.")
	}

	let basePreset: PresetItem | undefined = undefined
	if (!options.skipPreset && refImages.every((ref) => ref.adjustments === undefined)) {
		basePreset = await selectPresetForStyleCreation()
	}

	return createStyleDraftFromJpegReferences({
		styleName: options.styleName,
		refImages,
		basePreset,
		onProgress: options.onProgress,
	})
}

/**
 * Persist a user LUT to the style LUT store (IndexedDB) so the publish flow
 * can find it. Checks embedded data first, then IndexedDB and catalog as
 * fallbacks. Returns true if the LUT was resolved and stored, false otherwise.
 */
async function resolveAndStoreStyleLut(
	lutId: string,
	embeddedLut: LookTable | undefined,
): Promise<boolean> {
	if (embeddedLut) {
		await storeStyleLut(embeddedLut)
		return true
	}
	const existing = (await getStyleLut(lutId)) ?? (await MainLUTResolver.get(lutId))
	if (existing) {
		await storeStyleLut(existing)
		return true
	}
	return false
}

const convertFilesToRefImages = async (
	styleCreationId: string,
	files: File[],
	onProgress?: (progressDelta: number) => void,
	onThumbnail?: (index: number, blob: Blob) => void,
) => {
	const queue = new PQueue({ concurrency: 2, autoStart: false })
	const results: RefImageItem[] = new Array(files.length)
	const lutResolutionById = new Map<string, boolean>()

	files.forEach((file, i) => {
		queue.add(async () => {
			if (!checkIfStyleCreationIsCurrent(styleCreationId)) {
				throw new Error("Style creation cancelled or restarted. Stopping current process.")
			}

			const extractedAdjustments = await extractAdjustmentsFromImage(file)

			const photoId = getFileId(file)
			const isHEIC = isHEICFile(file)
			const source = await getBitmapSourceFromBlob(file, isHEIC)

			let imageBitmap = await createImageBitmap(source)
			if (
				imageBitmap.width > STYLE_REFERENCE_MAX_LONGEST_SIDE ||
				imageBitmap.height > STYLE_REFERENCE_MAX_LONGEST_SIDE
			) {
				const targetSize = fitToSize(
					[imageBitmap.width, imageBitmap.height],
					[STYLE_REFERENCE_MAX_LONGEST_SIDE, STYLE_REFERENCE_MAX_LONGEST_SIDE],
				)

				imageBitmap.close()
				imageBitmap = await createImageBitmap(source, {
					resizeWidth: targetSize[0],
					resizeHeight: targetSize[1],
					resizeQuality: "high",
				})
			}

			if (onThumbnail) {
				const thumb = await resizeImageBitmapToSize({
					imageBitMap: imageBitmap,
					longestSide: 200,
					result: ["blob"],
					outputType: "image/jpeg",
					outputQuality: 0.75,
				})
				if (thumb.blob) {
					onThumbnail(i, thumb.blob)
				}
			}

			let adjustments = extractedAdjustments?.adjustments
			if (adjustments && isParamsEmpty(adjustments)) {
				adjustments = undefined
			}

			// Register embedded LUT data (e.g. from Polarr JPEG), or verify the referenced
			// LUT exists in indexeddb or local catalog.
			// If the LUT is unresolvable, discard all adjustments so the image falls back to
			// full color match — partial adjustments without their LUT would produce wrong results.
			if (adjustments?.userLutId) {
				const lutId = adjustments.userLutId
				let resolved = lutResolutionById.get(lutId)
				if (resolved === undefined) {
					resolved = await resolveAndStoreStyleLut(lutId, extractedAdjustments?.userLut)
					lutResolutionById.set(lutId, resolved)
				}
				if (!resolved) {
					console.warn(
						`[create-style-upload] userLutId ${lutId} not found, discarding adjustments`,
					)
					adjustments = undefined
				}
			}

			onProgress?.(1)

			results[i] = {
				bitmap: imageBitmap,
				photoId,
				adjustments,
				isPolarrAdjustments: extractedAdjustments?.isPolarrAdjustments,
				coverSourceBlob: file,
				coverSourceIsHEIC: isHEIC,
			}
		})
	})

	queue.start()
	await queue.onIdle()

	return results
}

export const checkIfStyleCreationIsCurrent = (styleCreationId: string) => {
	const currentStyleCreationId = store.get(currentStyleCreationIdAtom)
	return currentStyleCreationId === styleCreationId
}
export const createStyleDraftFromCGPhotos = async (options: {
	styleCreationId: string
	styleName: string
	photos: PhotoResponseDTO[]
	onProgress?: (progress: number) => void
}) => {
	const refImages: RefImageItem[] = []
	const lutResolutionById = new Map<string, boolean>()
	const total = options.photos.length

	for (let i = 0; i < total; i++) {
		const photo = options.photos[i]
		if (!photo) {
			continue
		}

		if (!checkIfStyleCreationIsCurrent(options.styleCreationId)) {
			// Style creation has been cancelled
			return
		}
		const url = formattedPSImagePathToUrl(photo.path_xxlarge)
		try {
			const response = await fetchWithNetworkBehavior(url, undefined, {
				networkIntent: "active",
			})
			const blob = await response.blob()
			const imageBitmap = await createImageBitmap(blob)
			const extractedAdjustments = await extractAdjustmentsFromImage(blob)
			let adjustments = extractedAdjustments?.adjustments
			if (adjustments && isParamsEmpty(adjustments)) {
				adjustments = undefined
			}
			if (adjustments?.userLutId) {
				const lutId = adjustments.userLutId
				let resolved = lutResolutionById.get(lutId)
				if (resolved === undefined) {
					resolved = await resolveAndStoreStyleLut(lutId, extractedAdjustments?.userLut)
					lutResolutionById.set(lutId, resolved)
				}
				if (!resolved) {
					console.warn(
						`[create-style-cg] userLutId ${lutId} not found, discarding adjustments`,
					)
					adjustments = undefined
				}
			}
			const refImage: RefImageItem = {
				bitmap: imageBitmap,
				photoId: photo.id,
				adjustments,
				isPolarrAdjustments: extractedAdjustments?.isPolarrAdjustments,
				coverSourceBlob: blob,
				coverSourceIsHEIC: false,
			}
			refImages.push(refImage)
			options.onProgress?.(1)
		} catch (error) {
			console.error(`Failed to download or process photo ${photo.name}:`, error)
		}
	}

	if (refImages.length === 0) {
		throw new Error("No valid reference images found.")
	}

	if (!checkIfStyleCreationIsCurrent(options.styleCreationId)) {
		// Style creation has been cancelled
		return
	}
	let basePreset: PresetItem | undefined = undefined
	if (refImages.every((ref) => ref.adjustments === undefined)) {
		basePreset = await selectPresetForStyleCreation()
	}
	return createStyleDraftFromJpegReferences({
		styleName: options.styleName,
		refImages,
		basePreset,
		onProgress: options.onProgress,
	})
}

export function isAdjustmentsBW(adjustments: Partial<Adjustments>) {
	return (
		(adjustments.saturation ?? 0) < -0.8 ||
		(adjustments.vibrance ?? 0) < -0.9 ||
		(adjustments.saturation ?? 0) + (adjustments.vibrance ?? 0) < -1.4 ||
		adjustments.colorProfile === ColorProfileName.Monochrome ||
		adjustments.colorProfile === ColorProfileName.StandardMonochrome
	)
}

function removeRefWithBWAdjustments(allRefImages: RefImageItem[]) {
	let refImages = allRefImages.filter((refImage) => {
		const { adjustments } = refImage
		if (!adjustments) return true
		return !isAdjustmentsBW(adjustments)
	})
	if (refImages.length === 0) {
		logAiInfo(
			"[removeBWAdjustments] No color reference images found, use bw ref images instead",
		)
		refImages = allRefImages
	}
	logAiInfo(
		`[removeBWAdjustments] Found ${refImages.length}/${allRefImages.length} color reference images:`,
		refImages.map((refImage) => refImage.photoId),
	)
	return refImages
}

function removeNearlyBWRef(referencesMap: Record<string, ImageInfoRecord>) {
	const bwRefIds: string[] = []
	for (const [refId, imageInfo] of Object.entries(referencesMap)) {
		const isBw = isNearlyBW(imageInfo.colorMatchReference?.satLumStats)
		if (isBw) {
			bwRefIds.push(refId)
		}
	}
	logAiInfo(
		`[removeBWRefWithLowSaturation] Found ${bwRefIds.length}/${
			Object.keys(referencesMap).length
		} BW reference images:`,
		bwRefIds.map((refId) => referencesMap[refId]),
	)
	if (bwRefIds.length > 0 && bwRefIds.length < Object.keys(referencesMap).length) {
		for (const refId of bwRefIds) {
			delete referencesMap[refId]
		}
	}
}

/**
 * Normalizes HSL adjustments across reference images to improve transferability.
 *
 * This function analyzes the distribution of HSL values across all reference images
 * and clamps extreme values that are likely too photo-specific to transfer well.
 *
 * Strategy:
 * First phase (normalize HSL values):
 * 1. Collect all HSL values across images to understand the distribution
 * 2. Group images by base preset (using curve parameters as identifiers, and saturationOrange as secondary identifier)
 * 3. Calculate preset-specific statistics for HSL parameters
 * 4. Use IQR (Interquartile Range) method to identify normal vs extreme values per preset
 * 5. Fall back to global statistics for presets with insufficient samples
 * 6. Apply soft clamping to moderately extreme values (reduce by 60%)
 * 7. Hard clamp truly extreme values to the normal range
 * Second phase:
 * 1. Average adjustments within each preset group for consistency
 * 2. Replace small groups with majority preset
 */
function normalizeAdjustments(refImages: RefImageItem[]) {
	// HSL color channels
	const hslColors = [
		"Red",
		"Orange",
		"Yellow",
		"Green",
		"Aqua",
		"Blue",
		"Purple",
		"Magenta",
	] as const
	const hslTypes = ["hue", "saturation", "luminance"] as const
	const curveKeys = ["curvesAll", "curvesRed", "curvesGreen", "curvesBlue"] as const

	// Group images by preset using curve parameters as identifiers
	let presetGroups: Record<string, RefImageItem[]> = {}

	// Group images by preset signature
	for (const refImage of refImages) {
		const { adjustments } = refImage
		if (!adjustments) continue
		if (isParamsEmpty(adjustments)) {
			// empty
			refImage.adjustments = undefined
			continue
		}
		logAiInfo("Found non-empty adjustments", refImage.photoId, adjustments)

		// Create preset signature from curve parameters
		const curveSignature = curveKeys
			.map((curveKey) => {
				const curve = adjustments[curveKey] as any
				if (Array.isArray(curve) && curve.length >= 2) {
					// Use first few points of the curve as signature
					return `${curveKey}:${curve
						.slice(0, Math.min(4, curve.length))
						.map((p: any) =>
							Array.isArray(p)
								? `${Math.round(p[0])},${Math.round(p[1])}`
								: String(p),
						)
						.join("|")}`
				}
				return `${curveKey}:default`
			})
			.join("_")

		const presetKey = curveSignature || "unknown_preset"

		if (!presetGroups[presetKey]) {
			presetGroups[presetKey] = []
		}
		presetGroups[presetKey]?.push(refImage)
	}

	// Further subdivide preset groups by saturationOrange clustering
	// because sometimes saturationOrange is used to fix indoor color issues, and it's important to separate them as different presets
	const clusteredPresetGroups: Record<string, typeof refImages> = {}

	for (const [presetKey, images] of Object.entries(presetGroups)) {
		if (images.length <= 1) {
			// No need to cluster single image groups
			clusteredPresetGroups[presetKey] = images
			continue
		}

		// Extract saturationOrange values
		const satOrangeValues = images
			.map((img, idx) => ({
				value: img.adjustments?.saturationOrange ?? 0,
				index: idx,
				image: img,
			}))
			.sort((a, b) => a.value - b.value)

		// Simple clustering based on gaps in saturationOrange values
		const clusters: Array<typeof images> = []
		const threshold = 0.15 // Minimum gap to create a new cluster

		let currentCluster: typeof images = [satOrangeValues[0]?.image as RefImageItem]
		let lastValue = satOrangeValues[0]?.value ?? 0

		for (let i = 1; i < satOrangeValues.length; i++) {
			const currentValue = satOrangeValues[i]?.value ?? 0
			const gap = Math.abs(currentValue - lastValue)

			if (gap > threshold) {
				// Start a new cluster
				clusters.push(currentCluster)
				currentCluster = [satOrangeValues[i]?.image as RefImageItem]
			} else {
				// Add to current cluster
				currentCluster.push(satOrangeValues[i]?.image as RefImageItem)
			}
			lastValue = currentValue
		}

		// Add the last cluster
		clusters.push(currentCluster)

		// Create sub-groups with saturationOrange cluster suffix
		if (clusters.length > 1) {
			clusters.forEach((cluster, clusterIdx) => {
				const avgSatOrange =
					cluster.reduce(
						(sum, img) => sum + (img.adjustments?.saturationOrange ?? 0),
						0,
					) / cluster.length
				const clusterKey = `${presetKey}_satOrange_${clusterIdx}_${avgSatOrange.toFixed(2)}`
				clusteredPresetGroups[clusterKey] = cluster
			})

			logAiInfo(
				`[normalizeAdjustments] Split preset group "${presetKey}" into ${clusters.length} saturationOrange clusters:`,
				clusters.map((cluster, idx) => ({
					cluster: idx,
					size: cluster.length,
					avgSatOrange: (
						cluster.reduce(
							(sum, img) => sum + (img.adjustments?.saturationOrange ?? 0),
							0,
						) / cluster.length
					).toFixed(2),
				})),
			)
		} else {
			// No clustering needed, keep original group
			clusteredPresetGroups[presetKey] = images
		}
	}

	// Replace original preset groups with clustered groups
	presetGroups = clusteredPresetGroups

	logAiInfo(
		`[normalizeAdjustments] Found ${
			Object.keys(presetGroups).length
		} preset groups after saturationOrange clustering`,
	)
	if (Object.keys(presetGroups).length === 0) {
		// no valid adjustments found in all ref images
		return
	}

	// Calculate global statistics for fallback
	const globalStats: HslStats = calculateHslStatisticsFromImages(refImages)

	// Safety check: if no global stats available, we can't normalize anything
	if (Object.keys(globalStats).length === 0) {
		logAiInfo(
			"[normalizeAdjustments] No HSL values found in any reference images, skipping normalization",
		)
		return
	}

	// Calculate preset-specific statistics
	const presetStats: Record<string, PresetStatsData> = {}
	const minSamplesForPresetStats = 3 // Minimum samples needed for preset-specific stats

	for (const [presetKey, images] of Object.entries(presetGroups)) {
		// Calculate preset-specific statistics (always calculate, fallback decision made per parameter)
		const presetStatsData: HslStats = calculateHslStatisticsFromImages(images)
		const hasValidPresetStats = Object.keys(presetStatsData).length > 0

		presetStats[presetKey] = {
			stats: presetStatsData,
			sampleCount: images.length,
			useGlobalFallback: images.length < minSamplesForPresetStats || !hasValidPresetStats,
		}

		logAiInfo(
			`[normalizeAdjustments] presetStats`,
			presetStats[presetKey],
			"images:",
			images.map((img) => img.photoId),
		)
	}

	logAiInfo(`[normalizeAdjustments] Global parameterStats`, globalStats)

	// Apply normalization to each reference image using preset-specific stats with selective global fallback
	for (const [presetKey, images] of Object.entries(presetGroups)) {
		const presetData = presetStats[presetKey]
		if (!presetData) continue

		for (const refImage of images) {
			const { adjustments } = refImage
			if (!adjustments) continue

			let wasModified = false
			const normalized = { ...adjustments }

			for (const color of hslColors) {
				for (const type of hslTypes) {
					const key = `${type}${color}` as keyof Adjustments
					const value = normalized[key] as number

					if (typeof value !== "number") continue

					const result = normalizeHslParameter(
						key,
						value,
						presetData,
						globalStats,
						presetKey,
					)

					if (result.wasModified) {
						wasModified = true
					}

					if (Math.abs(result.newValue - value) > 0.001) {
						;(normalized as any)[key] = result.newValue
					}
				}
			}

			if (wasModified) {
				refImage.adjustments = normalized
			}
		}
	}

	// Second phase: Average adjustments within each preset group for consistency
	averageAdjustmentsWithinPresets(presetGroups)

	// Third phase: For images without lrAdjustments, copy from majority preset group
	copyMajorityGroupAdjustmentsToMissing(refImages, presetGroups)
}

/**
 * Average all adjustments within each preset group and replace small groups with majority preset
 * This ensures consistent base presets for AI style creation
 */
export function averageAdjustmentsWithinPresets(
	presetGroups: Record<string, Pick<RefImageItem, "photoId" | "adjustments">[]>,
) {
	// Find the majority group (largest group)
	const groupSizes = Object.entries(presetGroups).map(([key, images]) => ({
		key,
		size: images.length,
	}))
	const majorityGroup = groupSizes.reduce((max, current) =>
		current.size > max.size ? current : max,
	)
	const majorityThreshold = Math.max(1, Math.ceil(majorityGroup.size * 0.25)) // 25% of majority

	logAiInfo(
		`[averageAdjustmentsWithinPresets] Majority group: ${majorityGroup.key.substring(
			0,
			30,
		)}... (${majorityGroup.size} images)`,
	)
	logAiInfo(
		`[averageAdjustmentsWithinPresets] Small group threshold: ${majorityThreshold} images`,
	)

	// Parameters to exclude from averaging (curves and other non-transferable params)
	const excludeKeys = new Set([
		"curvesAll",
		"curvesRed",
		"curvesGreen",
		"curvesBlue",
		"pointColors",
		"colorProfile",
		"temperature",
		"tint",
		"exposure",
	])

	// First pass: Calculate averaged adjustments for each group
	const groupAverages: Record<string, Partial<Adjustments>> = {}

	for (const [presetKey, images] of Object.entries(presetGroups)) {
		const averagedAdjustments: Partial<Adjustments> = {}

		// Get all possible adjustment keys from the first image as a template
		const sampleAdjustments = images.find((img) => img.adjustments)?.adjustments
		if (!sampleAdjustments) {
			continue
		}

		// Average all parameters except excluded ones
		for (const key of Object.keys(sampleAdjustments) as Array<keyof Adjustments>) {
			if (excludeKeys.has(key as string)) {
				// Keep the original value for excluded parameters (curves, etc.)
				;(averagedAdjustments as any)[key] = sampleAdjustments[key]
				continue
			}

			const values: number[] = []

			// Collect all values for this parameter from images in this preset group
			for (const refImage of images) {
				const { adjustments } = refImage
				if (adjustments) {
					const value = adjustments[key]
					if (typeof value === "number") {
						// only average numbers
						values.push(value)
					}
				}
			}

			// Calculate average if we have values
			if (values.length > 0) {
				const average = values.reduce((sum, val) => sum + val, 0) / values.length
				;(averagedAdjustments as any)[key] = average
			}
		}

		groupAverages[presetKey] = averagedAdjustments
		logAiInfo(
			`[averageAdjustmentsWithinPresets] Calculated averages for preset with refId`,
			images[0]?.photoId,
			averagedAdjustments,
		)
	}

	// Second pass: Apply adjustments, replacing small groups with majority group's preset
	for (const [presetKey, images] of Object.entries(presetGroups)) {
		const isSmallGroup = images.length < majorityThreshold
		const adjustmentsToUse = isSmallGroup
			? groupAverages[majorityGroup.key]
			: groupAverages[presetKey]

		if (!adjustmentsToUse) {
			logAiInfo(
				`[averageAdjustmentsWithinPresets] No adjustments available for preset from refId`,
				images[0]?.photoId,
			)
			continue
		}

		// Apply the adjustments to all images in this preset group
		for (const refImage of images) {
			if (!refImage.adjustments) {
				refImage.adjustments = {}
			}

			// Update with averaged/majority values
			for (const [key, value] of Object.entries(adjustmentsToUse)) {
				if (value !== undefined) {
					;(refImage.adjustments as any)[key] = value
				}
			}
		}
	}
}

/**
 * Copies the finalized lrAdjustments of the majority preset group to any images
 * that do not have lrAdjustments.
 *
 * Should be called AFTER averageAdjustmentsWithinPresets so that the majority
 * group's adjustments represent the final averaged values.
 */
function copyMajorityGroupAdjustmentsToMissing(
	refImages: RefImageItem[],
	presetGroups: Record<string, RefImageItem[]>,
) {
	if (!refImages || refImages.length === 0) return

	// Determine majority group from provided presetGroups
	const groupEntries = Object.entries(presetGroups)
	if (groupEntries.length === 0) return

	const majority = groupEntries
		.map(([key, images]) => ({ key, size: images.length }))
		.reduce((max, curr) => (curr.size > max.size ? curr : max))

	const majorityImages = presetGroups[majority.key] ?? []
	const sourceAdjustments = majorityImages.find((img) => img.adjustments)?.adjustments
	if (!sourceAdjustments) {
		logAiInfo(
			`[copyMajorityGroupAdjustmentsToMissing] Majority group has no adjustments, skip copying`,
		)
		return
	}

	let copiedCount = 0
	for (const refImage of refImages) {
		const hasAdjustments =
			!!refImage.adjustments && Object.keys(refImage.adjustments).length > 0
		if (!hasAdjustments) {
			refImage.adjustments = structuredClone(sourceAdjustments)
			copiedCount += 1
		}
	}

	if (copiedCount > 0) {
		logAiInfo(
			`[copyMajorityGroupAdjustmentsToMissing] Copied majority adjustments to ${copiedCount} images`,
		)
	}
}

/**
 * Calculate HSL statistics from a list of reference images
 * This method is reusable for both global statistics and preset-specific statistics
 */
function calculateHslStatisticsFromImages(refImages: RefImageItem[]): HslStats {
	const hslColors = [
		"Red",
		"Orange",
		"Yellow",
		"Green",
		"Aqua",
		"Blue",
		"Purple",
		"Magenta",
	] as const
	const hslTypes = ["hue", "saturation", "luminance"] as const

	// Initialize HSL values collection
	const hslValues: Record<string, number[]> = {}
	for (const color of hslColors) {
		for (const type of hslTypes) {
			const key = `${type}${color}` as keyof Adjustments
			hslValues[key] = []
		}
	}

	// Collect HSL values from all reference images
	for (const refImage of refImages) {
		const { adjustments } = refImage
		if (!adjustments) continue

		for (const color of hslColors) {
			for (const type of hslTypes) {
				const key = `${type}${color}` as keyof Adjustments
				const value = adjustments[key] as number
				if (typeof value === "number" && hslValues[key]) {
					hslValues[key]?.push(value)
				}
			}
		}
	}

	// Calculate statistics for each HSL parameter
	const stats = calculateHslStatistics(hslValues)

	return stats
}

/**
 * Helper function to apply normalization to a single HSL parameter
 */
function normalizeHslParameter(
	key: string,
	value: number,
	presetData: PresetStatsData,
	globalStats: HslStats,
	presetKey: string,
): { newValue: number; wasModified: boolean } {
	// Determine whether to use global fallback for this specific parameter
	const presetParamStats: HslParameterStats | undefined = presetData.stats[key]
	const shouldUseGlobalFallback =
		!presetParamStats ||
		((presetParamStats.normalRange[1] > 0.35 || presetParamStats.normalRange[0] < -0.35) &&
			presetData.useGlobalFallback)

	const statsToUse: HslStats = shouldUseGlobalFallback ? globalStats : presetData.stats
	const paramStats: HslParameterStats | undefined = statsToUse[key]
	if (!paramStats) return { newValue: value, wasModified: false }

	const [normalMin, normalMax] = paramStats.normalRange
	const [extremeMin, extremeMax] = paramStats.extremeThreshold
	const fallbackIndicator = shouldUseGlobalFallback ? " (global fallback)" : ""

	let newValue = value
	let wasModified = false

	// If value is beyond extreme threshold, clamp to normal range
	if (value < extremeMin || value > extremeMax) {
		newValue = Math.max(normalMin, Math.min(normalMax, value))
		wasModified = true
		logAiInfo(
			`[normalizeAdjustments] Clamped extreme ${key}: ${value.toFixed(
				3,
			)} → ${newValue.toFixed(3)} (preset: ${presetKey.substring(
				0,
				20,
			)}...${fallbackIndicator})`,
		)
	}
	// If value is outside normal range but not extreme, apply soft clamping
	else if (value < normalMin || value > normalMax) {
		// Soft clamp: reduce the extreme portion by 60%
		if (value < normalMin) {
			const excess = normalMin - value
			newValue = normalMin - excess * 0.4
		} else {
			const excess = value - normalMax
			newValue = normalMax + excess * 0.4
		}
		wasModified = true
		logAiInfo(
			`[normalizeAdjustments] Soft clamped ${key}: ${value.toFixed(3)} → ${newValue.toFixed(
				3,
			)} (preset: ${presetKey.substring(0, 20)}...${fallbackIndicator})`,
		)
	}

	// Final safety clamp to absolute bounds
	newValue = Math.max(-1, Math.min(1, newValue))

	return { newValue, wasModified }
}

/**
 * Calculate HSL statistics for a given set of values
 */
function calculateHslStatistics(hslValues: Record<string, number[]>): HslStats {
	const parameterStats: HslStats = {}

	for (const [key, values] of Object.entries(hslValues)) {
		if (!values || values.length === 0) continue

		const sorted = [...values].sort((a, b) => a - b)
		const mean = values.reduce((sum, v) => sum + v, 0) / values.length
		const median = sorted[Math.floor(sorted.length / 2)] ?? 0

		// Calculate standard deviation
		const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length
		const std = Math.sqrt(variance)

		// Calculate quartiles
		const q25 = sorted[Math.floor(sorted.length * 0.25)] ?? 0
		const q75 = sorted[Math.floor(sorted.length * 0.75)] ?? 0
		const iqr = q75 - q25

		// Define normal transferable range (more conservative than typical range)
		// Use IQR method but cap at reasonable limits for transferability
		const iqrFactor = 0.5 // Less aggressive than typical 1.5 for outlier detection
		let normalMin = Math.max(q25 - iqr * iqrFactor, -0.8) // Cap at -0.8 for transferability
		let normalMax = Math.min(q75 + iqr * iqrFactor, 0.8) // Cap at 0.8 for transferability

		// Cap at 0.35 for transferability if not all reference images have extreme values
		normalMin = Math.max(normalMin, Math.min(q75, -0.35))
		normalMax = Math.min(normalMax, Math.max(q25, 0.35))

		// Extreme threshold - values beyond this are definitely too extreme
		const extremeMin = Math.max(normalMin - 0.2, -1.0)
		const extremeMax = Math.min(normalMax + 0.2, 1.0)

		parameterStats[key] = {
			mean,
			median,
			std,
			q25,
			q75,
			iqr,
			normalRange: [normalMin, normalMax],
			extremeThreshold: [extremeMin, extremeMax],
		}
	}

	return parameterStats
}

async function setAverageAdjustments(referencesMap: Record<string, ImageInfoRecord>) {
	// if they have presetId, return
	for (const refImage of Object.values(referencesMap)) {
		if (refImage.presetId) {
			return
		}
	}

	// if they don't have presetId, run AI color match on 4 sample images, each image find 1 cloesest reference images, so we get 4 resulted adjustments
	// then average them for HSL, and use the average as the base adjustments

	const HSL_KEYS = [
		"hueRed",
		"saturationRed",
		"luminanceRed",
		"hueOrange",
		"saturationOrange",
		"luminanceOrange",
		"hueYellow",
		"saturationYellow",
		"luminanceYellow",
		"hueGreen",
		"saturationGreen",
		"luminanceGreen",
		"hueAqua",
		"saturationAqua",
		"luminanceAqua",
		"hueBlue",
		"saturationBlue",
		"luminanceBlue",
		"huePurple",
		"saturationPurple",
		"luminancePurple",
		"hueMagenta",
		"saturationMagenta",
		"luminanceMagenta",
	] as const
	type HslKey = (typeof HSL_KEYS)[number]
	const averageHSL: Record<HslKey, number> = Object.fromEntries(
		HSL_KEYS.map((k) => [k, 0]),
	) as Record<HslKey, number>

	let sampleCount = 0
	const sampleWeights: Record<HslKey, number> = Object.fromEntries(
		HSL_KEYS.map((k) => [k, 0]),
	) as Record<HslKey, number>

	// Try to load pre-computed sample image infos first
	let sampleImageInfos = await loadSampleImageInfos()

	// Fallback to runtime computation if pre-computed data is not available
	if (Object.keys(sampleImageInfos).length === 0) {
		console.warn("Pre-computed sample image infos not found, computing at runtime...")
		sampleImageInfos = await generateSampleImageInfos()
	}

	for (const imageInfo of Object.values(sampleImageInfos)) {
		const distance = getDistanceMap(imageInfo, referencesMap)
		const kNeighbors = getKNN(distance)
		const closestRef = getValidNearestRef(kNeighbors, referencesMap)

		logAiInfo("KNN for sample input", {
			imageId: imageInfo.imageId,
			kNeighbors,
			closestRefId: closestRef?.imageId,
		})

		const closestRefWebpData = closestRef
			? await getReferenceWebpData256x256(closestRef).catch((error) => {
					console.warn("Failed to hydrate sample closest reference WebP", {
						imageId: closestRef.imageId,
						error,
					})
					return null
				})
			: null
		const inputWebpData = await getReferenceWebpData256x256(imageInfo).catch((error) => {
			console.warn("Failed to hydrate sample input WebP", {
				imageId: imageInfo.imageId,
				error,
			})
			return null
		})

		if (!closestRefWebpData || !inputWebpData) {
			continue
		}

		const refBitmap = await webpDataToBitmap(closestRefWebpData)
		const inputBitmap = await webpDataToBitmap(inputWebpData)

		const luts = await AI.computeNeuralColorMatchLUTs({
			inputImage: inputBitmap,
			referenceImage: refBitmap,
		})

		// Estimate Parameters
		const referenceFeaturesList: ReferenceColorMatchFeatures[] = []
		const referenceWbDist: number[] = []
		const weights: number[] = []
		for (const [refId, dist] of kNeighbors) {
			const ref = referencesMap[refId]
			if (ref?.colorMatchReference) {
				referenceFeaturesList.push(ref.colorMatchReference)
				weights.push(Math.max(0.2, 1 - (dist[4] ?? 0)))
				referenceWbDist.push(imageWbDistance(imageInfo.features, ref.features))
			}
		}
		const inputFeatures = getInputColorMatchFeatureWithFaceData(
			imageInfo,
		) as InputColorMatchFeaturesWithFaceData
		const referenceFeatures = getWeightedRefFeatures(
			referenceFeaturesList,
			weights,
			inputFeatures.avgFaceHsvl,
			referenceWbDist,
		)

		const unstretchedSize = fitToSize(imageInfo.features.imageSize, [300, 300])
		const resizedInputBitmap = resizeImageBitmapToBitmap(
			inputBitmap,
			unstretchedSize[0],
			unstretchedSize[1],
		)

		const { adjustmentsSkinToneOptimized } = estimateNeuralColorMatchParams({
			inputImageBitmap: resizedInputBitmap,
			luts,
			asShotWB: {
				asShotTemp: 5000,
				asShotTint: 0,
			},
			profileData: {
				profileCurve: null,
				colorLookTable: null,
				profileLookTable: null,
				userLookTable: null,
			},
			inputFeatures,
			referenceFeatures,
			computeFullParams: true,
			keyIsRef: false,
			computeWb: true,
		})
		resizedInputBitmap.close()
		inputBitmap.close()
		refBitmap.close()

		// weight more for green/yellow hue/saturation when vegetation correction is present
		const hasVegeCorrection =
			!!imageInfo.colorMatchReference?.vegetationHsv && !!referenceFeatures.vegetationHsv
		const vegeWeight = hasVegeCorrection ? 1.0 : 0.0

		logAiInfo("adjustments for sample input", {
			adjustmentsSkinToneOptimized,
			referenceFeatures,
			hasVegeCorrection,
		})

		// Extract vegetation-related values for weighting
		const hueGreen = (adjustmentsSkinToneOptimized as any)["hueGreen"] as number | undefined
		const hueYellow = (adjustmentsSkinToneOptimized as any)["hueYellow"] as number | undefined
		const satGreen = (adjustmentsSkinToneOptimized as any)["saturationGreen"] as
			| number
			| undefined
		const satYellow = (adjustmentsSkinToneOptimized as any)["saturationYellow"] as
			| number
			| undefined

		const hueScale = 4.0 // Scale factor for positive hue contributions
		const satScale = 4.0 // Scale factor for negative saturation contributions

		for (const key of HSL_KEYS) {
			const val = (adjustmentsSkinToneOptimized as any)[key]
			if (typeof val === "number") {
				let weight = 1.0 // Base weight

				// Add vegetation weight based on parameter patterns
				if (key === "hueGreen" && typeof hueGreen === "number") {
					weight += Math.max(hueGreen, 0) * hueScale + vegeWeight
				}
				if (key === "saturationGreen" && typeof satGreen === "number") {
					weight += Math.max(-satGreen, 0) * satScale + vegeWeight
				}
				if (key === "hueYellow" && typeof hueYellow === "number") {
					weight += Math.max(hueYellow, 0) * hueScale + vegeWeight
				}
				if (key === "saturationYellow" && typeof satYellow === "number") {
					weight += Math.max(-satYellow, 0) * satScale + vegeWeight
				}

				averageHSL[key] += val * weight
				sampleWeights[key] += weight
			}
		}
		sampleCount++
	}

	if (sampleCount > 0) {
		for (const key of HSL_KEYS) {
			// Use weighted average instead of simple average
			if (sampleWeights[key] > 0) {
				averageHSL[key] /= sampleWeights[key]
			}
		}
	}

	logAiInfo("averageHSL from sample inputs", averageHSL)

	for (const refImage of Object.values(referencesMap)) {
		if (refImage.presetId) {
			continue
		}
		if (refImage.colorMatchReference) {
			refImage.colorMatchReference.averageAdjustments = averageHSL
		}
	}
}
